import {
  QR_BASE64,
  AZTEC_BASE64,
  CODE128_BASE64,
  PDF417_BASE64,
} from '../../../../utils/base64';
import { CustomTrainIcon } from '../../../../theme/icons/CustomTrainIcon';
import { CustomBusIcon } from '../../../../theme/icons/CustomBusIcon';
import { CustomFerryIcon } from '../../../../theme/icons/CustomFerryIcon';
import { CustomAirplaneIcon } from '../../../../theme/icons/CustomAirplaneIcon';
import { CustomArrowIcon } from '../../../../theme/icons/CustomArrowIcon';
import { PreviewData } from '../../../../store/types/template';

export type PassType =
  | 'loyalty'
  | 'stampCard'
  | 'eventTicket'
  | 'generic'
  | 'coupon'
  | 'transit'
  | string;

export type BarcodeType = 'code128' | 'pdf417' | 'aztec' | 'qr' | string;

export const getPassDisplayConfig = (passType?: PassType) => {
  const thumbnailDisplay =
    passType === 'loyalty' ||
    passType === 'stampCard' ||
    passType === 'eventTicket' ||
    passType === 'transit'
      ? 'none'
      : '';

  const bannerDisplay =
    passType === 'generic' || passType === 'transit' ? 'none' : '';

  const primaryDisplay =
    passType === 'generic' || passType === 'transit' ? '' : 'none';

  const labelDisplay =
    passType === 'loyalty' || passType === 'stampCard' || passType === 'coupon'
      ? ''
      : 'none';

  const borderRadius =
    passType === 'eventTicket' || passType === 'coupon' ? '' : '4%';

  return {
    thumbnailDisplay,
    bannerDisplay,
    primaryDisplay,
    labelDisplay,
    borderRadius,
  };
};

export const getPreviewBarcodeDimensions = (type?: BarcodeType) => {
  if (type === 'code128' || type === 'pdf417') {
    return { width: '200px', height: '60px' };
  }

  if (type === 'aztec' || type === 'qr') {
    return { width: '100px', height: '100px' };
  }

  return { width: '', height: '' };
};

export const getPreviewBarcodeImage = (type?: BarcodeType) => {
  if (type === 'code128') return CODE128_BASE64;
  if (type === 'pdf417') return PDF417_BASE64;
  if (type === 'aztec') return AZTEC_BASE64;
  if (type === 'qr') return QR_BASE64;
  return null;
};

export const getPreviewTransitIcon = (
  transitType?: string,
  titleFontColor?: string,
  templatesPage?: boolean
) => {
  const boxSize = templatesPage ? '40px' : '35px';
  switch (transitType) {
    case 'train':
      return <CustomTrainIcon boxSize={boxSize} color={titleFontColor} />;
    case 'bus':
      return <CustomBusIcon boxSize={boxSize} color={titleFontColor} />;
    case 'ferry':
      return <CustomFerryIcon boxSize={boxSize} color={titleFontColor} />;
    case 'airplane':
      return <CustomAirplaneIcon boxSize={boxSize} color={titleFontColor} />;
    default:
      return <CustomArrowIcon boxSize={boxSize} color={titleFontColor} />;
  }
};

export type BackField = {
  name: 'backOne' | 'backTwo' | 'backThree' | 'backFour' | 'backFive' | string;
  value?: string;
};

export const buildBackFields = (previewData: PreviewData): BackField[] => [
  { name: 'backOne', value: previewData.backOneLabel },
  { name: 'backTwo', value: previewData.backTwoLabel },
  { name: 'backThree', value: previewData.backThreeLabel },
  { name: 'backFour', value: previewData.backFourLabel },
  { name: 'backFive', value: previewData.backFiveLabel },
];

export const filterBackFields = (
  previewData: PreviewData,
  backFields: BackField[]
): BackField[] =>
  backFields.filter((field) => {
    const suffix = field.name.replace('back', '').toLowerCase();
    const baseKey = `back.${suffix}`;
    return (
      !!previewData[`${field.name}Label` as keyof PreviewData] ||
      !!previewData[`${field.name}Value` as keyof PreviewData] ||
      !!previewData.isFieldHighlighted?.(`${baseKey}.id`) ||
      !!previewData.isFieldHighlighted?.(`${baseKey}.label`) ||
      !!previewData.isFieldHighlighted?.(`${baseKey}.defaultValue`)
    );
  });

type IsFieldHighlightedFn = ((path: string) => boolean) | undefined;

export const getBackFieldHighlightStyles = (
  fieldName: string,
  isFieldHighlighted: IsFieldHighlightedFn
) => {
  const suffix = fieldName.replace('back', '').toLowerCase();
  const baseKey = `back.${suffix}`;

  const highlighted =
    isFieldHighlighted?.(`${baseKey}.id`) ||
    isFieldHighlighted?.(`${baseKey}.label`) ||
    isFieldHighlighted?.(`${baseKey}.defaultValue`);

  return {
    border: highlighted ? '2px solid #FF4935' : 'none',
    padding: highlighted ? '4px' : '0',
  };
};

export const getAppleWatermarkConfig = (previewData: PreviewData) => {
  if (previewData.appleBannerImage) {
    return {
      color: previewData.appleBannerWatermarkColor,
      position: previewData.appleBannerWatermarkPosition,
    };
  }

  return {
    color: previewData.watermarkColor,
    position: previewData.watermarkPosition,
  };
};

export const getApplePreviewNotificationSubtitle = (
  previewData: PreviewData
): string => {
  const raw = previewData.notificationMessage;
  if (raw != null && raw !== '') return raw;
  return raw ?? 'Your message text here';
};
